import { Fragment, useState } from 'react';
import { FunnelIcon } from '@heroicons/react/24/solid';
import cx from 'classnames';

import { Header } from '../../components/Header';
import { PageMetadata } from '../../components/PageMetadata';
import { Footer } from '../../components/Footer/Footer';
import { Section, Row, Column } from '../../components/Section';
import { CallToActionSection } from '../../components/CallToActionSection';

import {
  getServerPageAgentsList,
  getServerPageAgentsFilters,
  useAgentsListLazyQuery,
  getServerPageAgentsReviews,
  getServerPageAgents,
} from './agents.generated';
import { apolloClient, GetServerPageProps } from '../../lib/apollo';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { AgentCard } from './AgentCard';
import { AgentFilter } from './AgentFilter';
import {
  getCalendlyEventUris,
  lookupCalendlyAvailability,
} from '../../lib/calendly';
import type {
  CalendlyAvailableTimes,
  CalendlyEventUris,
} from '../../lib/calendly';
import { Sidebar } from '../../components/Sidebar';
import { useDays } from './useDays';
import {
  generateAgentRandomSortOrder,
  agentSort,
  useAgentSort,
  AgentRandomSortMap,
} from './useAgentSort';
import { AgentsHeader } from './AgentsHeader';
import { AgentsMobileScheduling } from './AgentsMobileScheduling';
import { AgentSchedulingDay } from './AgentSchedulingDay';
import { AgentsDayHeaders } from './AgentsDayHeaders';
import { useUpdateCalendlyAvailability } from '../../hooks/useUpdateCalendlyAvailability';
import { aggregateReviews } from '../../lib/reviewAggregation';
import { ServerTimezoneAlert } from '../../components/ServerTimezoneAlert';
import { CalendlyModal } from '../../components/CalendlyModal';
import { SeeFullCalendar } from './SeeFullCalendar';

export default function AgentsPage({
  pageMetadata,
  list,
  filters,
  agentRandomSortOrder,
  agentCalendlySchedulingUrls,
  calendlyEventUris,
  calendlyAvailableTimes: calendlyAvailableTimesFallback,
  aggregatedReviews,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  if (!pageMetadata) {
    throw new Error('Unable to load page');
  }

  const days = useDays({ numberOfDays: 5 });

  const [activeFilters, setActiveFilters] = useState({
    products: [],
    carriers: [],
    certifications: [],
    languages: [],
  });
  const [calendlyModalUri, setCalendlyModalUri] = useState<string>();
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);

  const initialAgents = list.data.agentCollection?.items;

  const [updateFilteredAgents, { data }] = useAgentsListLazyQuery({
    variables: {
      filters: {
        products_contains_some:
          activeFilters.products.length > 0 ? activeFilters.products : null,
        insuranceCarrier_contains_some:
          activeFilters.carriers.length > 0 ? activeFilters.carriers : null,
        certifications_contains_some:
          activeFilters.certifications.length > 0
            ? activeFilters.certifications
            : null,
        languages_contains_some:
          activeFilters.languages.length > 0 ? activeFilters.languages : null,
      },
    },
    client: apolloClient, // Manually providing this here instead of using <ApolloProvider /> to keep Apollo out of the main bundle since very few pages load data client-side
  });

  const sortedFilteredAgents = useAgentSort({
    agents: data?.agentCollection?.items.flatMap((agent) => agent ?? []),
    agentRandomSortOrder,
  });

  const agents =
    sortedFilteredAgents ??
    initialAgents?.flatMap((agent) => agent ?? []) ??
    [];

  const allAgentsForFilters =
    filters.data.agentCollection?.items.flatMap((agent) => agent ?? []) ?? [];

  const applyFilters =
    (category: keyof typeof activeFilters) => (newSelection: string[]) => {
      setActiveFilters({
        ...activeFilters,
        [category]: newSelection,
      });
      updateFilteredAgents();
    };

  const { calendlyAvailableTimes } = useUpdateCalendlyAvailability({
    schedulingUrls: agentCalendlySchedulingUrls,
    calendlyEventUris,
    calendlyAvailableTimesFallback,
  });

  return (
    <>
      <PageMetadata
        pageTitle={pageMetadata.seoTitle ?? undefined}
        description={pageMetadata.seoDescription ?? undefined}
        openGraphImage={pageMetadata.openGraphImage?.url ?? undefined}
      />
      <Header />
      <AgentsHeader />
      <Section background="WHITE" borderless>
        <Row>
          <Column className="mt-0">
            <div className="grid lg:grid-cols-[max-content,auto] gap-4">
              <Sidebar
                title="Filters"
                open={filtersPanelOpen}
                setOpen={setFiltersPanelOpen}
              >
                <AgentFilter
                  label="Products"
                  agents={allAgentsForFilters}
                  filterField={({ products }) => products}
                  selected={activeFilters.products}
                  setSelected={applyFilters('products')}
                />
                <AgentFilter
                  label="Carrier"
                  agents={allAgentsForFilters}
                  filterField={({ insuranceCarrier }) => insuranceCarrier}
                  selected={activeFilters.carriers}
                  setSelected={applyFilters('carriers')}
                  sort
                />
                <AgentFilter
                  label="Certifications"
                  agents={allAgentsForFilters}
                  filterField={({ certifications }) => certifications}
                  selected={activeFilters.certifications}
                  setSelected={applyFilters('certifications')}
                />
                <AgentFilter
                  label="Languages"
                  agents={allAgentsForFilters}
                  filterField={({ languages }) => languages}
                  selected={activeFilters.languages}
                  setSelected={applyFilters('languages')}
                />
              </Sidebar>
              <div>
                <div className="flex justify-end mb-4">
                  <button
                    className="lg:hidden text-gray-600 "
                    onClick={() => setFiltersPanelOpen(true)}
                  >
                    Filters <FunnelIcon className="w-5 m-2 inline-block" />
                  </button>
                </div>
                <ServerTimezoneAlert />
                <div className="grid grid-flow-row auto-rows-min sm:grid-cols-[auto,repeat(2,7em)] md:grid-cols-[auto,repeat(3,7em)] xl:grid-cols-[auto,repeat(5,7em)]">
                  {agents.length > 0 ? (
                    <>
                      <AgentsDayHeaders
                        days={days}
                        agentCount={agents.length}
                      />
                      {agents.map((agent) => {
                        const calendlyUri = agent.calendlyUrl;
                        const calendlyUriFullCalendar =
                          agent.calendlyUrlFullCalendarOverride;
                        const agentAvailableTimes = calendlyUri
                          ? calendlyAvailableTimes?.[calendlyUri]
                          : undefined;

                        const today = days[0].toISODate();
                        const tomorrow = days[1].toISODate();

                        if (!today || !tomorrow) {
                          throw new Error(
                            'Unable to compute today or tomorrow for displaying agent directory',
                          );
                        }

                        const numTimeSlotsToday =
                          agentAvailableTimes?.days?.[today]?.totalTimeSlots ??
                          0;
                        const numTimeSlotsTomorrow =
                          agentAvailableTimes?.days?.[tomorrow]
                            ?.totalTimeSlots ?? 0;

                        // Number of consecutive days with 0 time slots
                        const upcomingDaysWithoutTimeSlots = days
                          .map(
                            (day) =>
                              agentAvailableTimes?.days?.[day.toISODate() ?? '']
                                ?.totalTimeSlots ?? 0,
                          )
                          .reduce(
                            (count, dayTimeSlots, index) =>
                              count === index && dayTimeSlots === 0
                                ? count + 1
                                : count,
                            0,
                          );

                        return (
                          <Fragment key={agent.sys.id}>
                            <div
                              className={cx('p-4 pb-0', {
                                'sm:border-b sm:mb-4 sm:pb-4':
                                  !calendlyUri || !agentAvailableTimes,
                              })}
                            >
                              <AgentCard
                                agent={agent}
                                aggregatedReviews={
                                  aggregatedReviews[agent.sys.id]
                                }
                              />
                              {calendlyUri && (
                                <AgentsMobileScheduling
                                  numTimeSlotsToday={numTimeSlotsToday}
                                  numTimeSlotsTomorrow={numTimeSlotsTomorrow}
                                  setCalendlyModalUri={setCalendlyModalUri}
                                  calendlyUri={calendlyUri}
                                  calendlyUriFullCalendar={
                                    calendlyUriFullCalendar ?? undefined
                                  }
                                  today={today}
                                  tomorrow={tomorrow}
                                />
                              )}
                            </div>
                            {calendlyUri && agentAvailableTimes ? (
                              <Fragment>
                                {days.map((dayDateTime, index) => {
                                  const day = dayDateTime.toISODate();
                                  if (!day) {
                                    throw new Error(
                                      'Unable to compute day for AgentSchedulingDay',
                                    );
                                  }

                                  return (
                                    <Fragment key={day}>
                                      <AgentSchedulingDay
                                        day={day}
                                        index={index}
                                        agentAvailableTimes={
                                          agentAvailableTimes
                                        }
                                        calendlyUri={calendlyUri}
                                        setCalendlyModalUri={
                                          setCalendlyModalUri
                                        }
                                        className={cx({
                                          'sm:hidden':
                                            upcomingDaysWithoutTimeSlots >= 2,
                                          'md:hidden':
                                            upcomingDaysWithoutTimeSlots >= 3,
                                          'xl:hidden':
                                            upcomingDaysWithoutTimeSlots >= 5,
                                        })}
                                      />
                                    </Fragment>
                                  );
                                })}
                                <div
                                  className={cx(
                                    'hidden flex-col justify-center items-center',
                                    {
                                      'sm:flex sm:col-span-2':
                                        upcomingDaysWithoutTimeSlots >= 2,
                                      'md:flex md:col-span-3':
                                        upcomingDaysWithoutTimeSlots >= 3,
                                      'xl:flex xl:col-span-5':
                                        upcomingDaysWithoutTimeSlots >= 5,
                                    },
                                    {
                                      'md:hidden':
                                        upcomingDaysWithoutTimeSlots >= 2 &&
                                        upcomingDaysWithoutTimeSlots < 3,
                                      'xl:hidden':
                                        upcomingDaysWithoutTimeSlots >= 3 &&
                                        upcomingDaysWithoutTimeSlots < 5,
                                    },
                                  )}
                                >
                                  <p className="text-gray-500 text-xs font-medium mb-2 text-center">
                                    No appointments available on these days
                                  </p>
                                  <SeeFullCalendar
                                    calendlyUriFullCalendar={
                                      calendlyUriFullCalendar
                                    }
                                    calendlyUri={calendlyUri}
                                    setCalendlyModalUri={setCalendlyModalUri}
                                  />
                                </div>
                                <div
                                  className={cx(
                                    'hidden sm:flex p-4 pt-0 border-b mb-4',
                                  )}
                                ></div>
                                <div
                                  className={cx(
                                    'hidden sm:flex justify-center sm:col-span-2 md:col-span-3 xl:col-span-5 pb-3 border-b mb-4',
                                  )}
                                >
                                  <div
                                    className={cx({
                                      'sm:hidden md:flex':
                                        upcomingDaysWithoutTimeSlots >= 2,
                                      'md:!hidden xl:flex':
                                        upcomingDaysWithoutTimeSlots >= 3,
                                      'xl:!hidden':
                                        upcomingDaysWithoutTimeSlots >= 5,
                                    })}
                                  >
                                    <SeeFullCalendar
                                      calendlyUriFullCalendar={
                                        calendlyUriFullCalendar
                                      }
                                      calendlyUri={calendlyUri}
                                      setCalendlyModalUri={setCalendlyModalUri}
                                    />
                                  </div>
                                </div>
                              </Fragment>
                            ) : (
                              <div className="sm:col-span-2 md:col-span-3 xl:col-span-5 border-b mb-4 pt-4 pb-8 flex justify-center items-center">
                                <div className="text-center text-gray-700 text-xs space-y-1">
                                  <p>Available Monday-Friday</p>
                                  <p>9am to 5pm</p>
                                </div>
                              </div>
                            )}
                          </Fragment>
                        );
                      })}
                    </>
                  ) : (
                    'No agents found. Try removing some filters.'
                  )}
                </div>
                <CalendlyModal
                  calendlyModalUri={calendlyModalUri}
                  onClose={() => setCalendlyModalUri(undefined)}
                />
              </div>
            </div>
          </Column>
        </Row>
      </Section>
      <CallToActionSection quoteAction />
      <Footer />
    </>
  );
}

interface AggregatedReviews {
  [agentId: string]: ReturnType<typeof aggregateReviews>;
}

export const getStaticProps: GetStaticProps<{
  pageMetadata:
    | NonNullable<
        NonNullable<
          GetServerPageProps<typeof getServerPageAgents>['data']
        >['pageCollection']
      >['items'][0]
    | undefined;
  list: GetServerPageProps<typeof getServerPageAgentsList>;
  filters: GetServerPageProps<typeof getServerPageAgentsFilters>;
  calendlyAvailableTimes: CalendlyAvailableTimes;
  calendlyEventUris: CalendlyEventUris;
  agentCalendlySchedulingUrls: string[];
  agentRandomSortOrder: AgentRandomSortMap;
  aggregatedReviews: AggregatedReviews;
}> = async () => {
  const list = (await getServerPageAgentsList({})).props;
  const agents =
    list.data.agentCollection?.items.flatMap((agent) => agent ?? []) ?? [];

  const agentRandomSortOrder = generateAgentRandomSortOrder(agents);

  const agentCalendlySchedulingUrls =
    list.data.agentCollection?.items.flatMap(
      (agent) => agent?.calendlyUrl ?? [],
    ) ?? [];
  const calendlyEventUris = await getCalendlyEventUris();
  const calendlyAvailableTimes = await lookupCalendlyAvailability(
    agentCalendlySchedulingUrls,
    calendlyEventUris,
  );

  const aggregatedReviews = Object.entries(
    // Load all reviews for all agents for local aggregation
    (
      await getServerPageAgentsReviews({})
    ).props.data.reviewAggregationCollection?.items.reduce<{
      [agentId: string]: NonNullable<
        NonNullable<
          NonNullable<
            GetServerPageProps<
              typeof getServerPageAgentsReviews
            >['data']['reviewAggregationCollection']
          >['items']
        >[0]
      >[];
    }>(
      // Group reviews by agent id
      (acc, review) =>
        review?.agent?.sys.id
          ? {
              ...acc,
              [review.agent.sys.id]: [
                ...(acc[review.agent.sys.id] ?? []),
                ...(review ? [review] : []),
              ],
            }
          : acc,
      {},
    ) ?? {},
  )
    // Aggregate each agent's reviews
    .reduce<AggregatedReviews>(
      (acc, [agentId, reviews]) => ({
        ...acc,
        [agentId]: aggregateReviews({
          reviews: { total: reviews.length, items: reviews },
          disableBuckets: true,
        }),
      }),
      {},
    );

  return {
    props: {
      list: {
        ...list,
        data: {
          ...list.data,
          agentCollection: {
            ...list.data.agentCollection,
            items:
              agentSort({
                agents,
                agentRandomSortOrder,
              }) ?? [],
          },
        },
      },
      pageMetadata: (await getServerPageAgents({})).props.data.pageCollection
        ?.items[0],
      filters: (await getServerPageAgentsFilters({})).props,
      agentRandomSortOrder,
      agentCalendlySchedulingUrls,
      calendlyEventUris,
      calendlyAvailableTimes,
      aggregatedReviews,
    },
    // Refresh random agent order every 3 minutes
    revalidate: 60 * 3,
  };
};
